/**
 * ChecklistHistorySheet - Versões publicadas e timeline de eventos do modelo
 *
 * @version 1.0.0
 * RELEASE: 2026-09-26 v1.0.0 - Criação inicial (Fase 1b)
 */
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useChecklistTimeline, useChecklistVersions } from '@/hooks/useChecklistTemplates';

/** Rótulos dos eventos gravados em checklist_events */
const EVENT_LABELS: Record<string, string> = {
  template_created: 'Modelo criado',
  template_updated: 'Configurações alteradas',
  template_activated: 'Modelo ativado',
  template_inactivated: 'Modelo inativado',
  section_created: 'Seção criada',
  section_updated: 'Seção alterada',
  section_deleted: 'Seção excluída',
  section_inactivated: 'Seção inativada',
  section_reactivated: 'Seção reativada',
  sections_reordered: 'Seções reordenadas',
  item_created: 'Item criado',
  item_updated: 'Item alterado',
  item_deleted: 'Item excluído',
  item_inactivated: 'Item inativado',
  item_reactivated: 'Item reativado',
  items_reordered: 'Itens reordenados',
  version_published: 'Versão publicada',
};

const fmt = (d: string) => format(new Date(d), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });

export function ChecklistHistorySheet({ open, onOpenChange, templateId }: {
  open: boolean; onOpenChange: (o: boolean) => void; templateId: string;
}) {
  const versions = useChecklistVersions(templateId, open);
  const timeline = useChecklistTimeline(templateId, open);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader><SheetTitle>Histórico do modelo</SheetTitle></SheetHeader>
        <Tabs defaultValue="versions" className="mt-4">
          <TabsList>
            <TabsTrigger value="versions">Versões</TabsTrigger>
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
          </TabsList>

          <TabsContent value="versions" className="space-y-2">
            {versions.isLoading ? <Skeleton className="h-20 w-full" /> :
              (versions.data ?? []).length === 0 ? <p className="text-sm text-muted-foreground">Nenhuma versão publicada.</p> :
              versions.data!.map((v) => (
                <div key={v.id} className="rounded-md border p-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Versão {v.version_number}</span>
                    {v.is_current && <Badge>Vigente</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {fmt(v.published_at)} · {v.published_by_name ?? 'Sistema'} · {v.items_count} itens
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    +{v.change_summary?.items_added ?? 0} adicionados · {v.change_summary?.items_changed ?? 0} alterados · −{v.change_summary?.items_removed ?? 0} retirados
                  </p>
                </div>
              ))}
          </TabsContent>

          <TabsContent value="timeline">
            {timeline.isLoading ? <Skeleton className="h-20 w-full" /> : (
              <ol className="relative ml-2 space-y-3 border-l pl-4">
                {(timeline.data ?? []).map((e) => (
                  <li key={e.id} className="text-sm">
                    <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border bg-background" />
                    <p className="font-medium">{EVENT_LABELS[e.event_type] ?? e.event_type}
                      {typeof e.metadata?.name === 'string' && <span className="font-normal text-muted-foreground"> — {e.metadata.name as string}</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">{fmt(e.created_at)} · {e.actor_full_name ?? 'Sistema'}</p>
                  </li>
                ))}
              </ol>
            )}
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
